import React, { useState, useEffect } from 'react';
import { compatApi } from '../../utils/Intercepter';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import { PURPLE, hexToRgba } from '../../utils/designUtils';
import useTokenStore from '../../stores/tokens';

const LEAVE_TYPE_LABEL = {
    ANNUAL:  '연차',
    HALF_AM: '반차 (오전)',
    HALF_PM: '반차 (오후)',
    PUBLIC:  '공가',
    SICK:    '병가',
    SUMMER:  '하계휴가',
    OTHER:   '기타',
};

const STATUS_CONFIG = {
    PENDING:  { label: '검토 중',  color: '#b45309', bg: '#fef3c7', dot: '#f59e0b', border: '#fde68a' },
    APPROVED: { label: '승인',     color: '#15803d', bg: '#dcfce7', dot: '#22c55e', border: '#86efac' },
    REJECTED: { label: '반려',     color: '#dc2626', bg: '#fee2e2', dot: '#ef4444', border: '#fca5a5' },
    DRAFT:    { label: '임시저장', color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af', border: '#e5e7eb' },
};

function StatusBadge({ status }) {
    const s = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, color: s.color, background: s.bg,
            padding: '2px 8px', borderRadius: 999,
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, display: 'inline-block' }}/>
            {s.label}
        </span>
    );
}

const CELL_LABEL = {
    padding: '8px 12px', backgroundColor: '#f4f5f7',
    fontSize: '0.78rem', fontWeight: 600, color: '#555',
    borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap',
    verticalAlign: 'middle', width: 1,
};
const CELL_VALUE = { padding: '8px 14px', fontSize: '0.875rem', color: '#222', verticalAlign: 'middle' };

function DocRow({ children }) {
    return <tr style={{ borderBottom: '1px solid #dee2e6' }}>{children}</tr>;
}
function Lbl({ children }) { return <td style={CELL_LABEL}>{children}</td>; }
function Val({ children, colSpan, style }) {
    return <td style={{ ...CELL_VALUE, ...style }} colSpan={colSpan}>{children ?? '-'}</td>;
}

// mode: 'inbox' | 'draft' | 'readonly'
export default function VacationDetailModal({ show, onHide, vacationId, mode, onAction }) {
    const [detail,        setDetail]        = useState(null);
    const [loading,       setLoading]       = useState(false);
    const [acting,        setActing]        = useState(false);
    const [copying,       setCopying]       = useState(false);
    const [rejectMode,    setRejectMode]    = useState(false);
    const [rejectComment, setRejectComment] = useState('');

    const user = useTokenStore((state) => state.user);
    const userEmail = user?.userEmail;
    const myApproverInfo = (detail?.approvers || []).find(a => a.approverEmail === userEmail);
    const isMyTurnPending = myApproverInfo && myApproverInfo.status === 'PENDING' && detail?.status === 'PENDING';

    useEffect(() => {
        if (!show || !vacationId) { setDetail(null); return; }
        setRejectMode(false);
        setRejectComment('');
        let cancelled = false;
        setLoading(true);
        setDetail(null);
        compatApi.get(`/approval/${vacationId}`)
            .then(res  => { if (!cancelled) setDetail(res.data); })
            .catch(()  => { if (!cancelled) setDetail(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [show, vacationId]);

    const handleApprove = async () => {
        if (!window.confirm('해당 문서를 승인하시겠습니까?')) return;
        setActing(true);
        try {
            await compatApi.post(`/approval/${vacationId}/approve`);
            toast.success('승인되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch {
            toast.error('승인에 실패했습니다.', toastConfig);
        } finally { setActing(false); }
    };

    const handleRejectConfirm = async () => {
        if (!window.confirm('해당 문서를 반려하시겠습니까?')) return;
        setActing(true);
        try {
            await compatApi.post(`/approval/${vacationId}/reject`, { comment: rejectComment || null });
            toast.success('반려되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch {
            toast.error('반려에 실패했습니다.', toastConfig);
        } finally { setActing(false); }
    };

    const handleCopy = async () => {
        setCopying(true);
        try {
            await compatApi.post('/approval/vacation', {
                organizationCode:  detail.organizationCode,
                leaveType:         detail.leaveType,
                startDt:           detail.startDt,
                endDt:             detail.endDt,
                reason:            detail.reason,
                remark:            detail.remark || null,
                agentEmail:        detail.agentEmail || null,
                emergencyContact:  detail.emergencyContact || null,
                emergencyRelation: detail.emergencyRelation || null,
                approvers: (detail.approvers || [])
                    .sort((a, b) => a.approverOrder - b.approverOrder)
                    .map((a, i) => ({ userEmail: a.approverEmail, order: i + 1 })),
                notiApprovalStatus: true,
                notiDayBefore:      false,
                notiDayOf:          false,
                notiApprover:       true,
                status: 'DRAFT',
            });
            toast.success('복사본이 임시 저장되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch {
            toast.error('복사에 실패했습니다.', toastConfig);
        } finally { setCopying(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm('임시저장 문서를 삭제하시겠습니까?')) return;
        setActing(true);
        try {
            await compatApi.del(`/approval/${vacationId}`);
            toast.success('삭제되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch {
            toast.error('삭제에 실패했습니다.', toastConfig);
        } finally { setActing(false); }
    };

    const statusCfg = detail
        ? (STATUS_CONFIG[detail.status] || { label: detail.status, color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af', border: '#e5e7eb' })
        : null;

    const workingDays = (() => {
        if (!detail?.startDt || !detail?.endDt) return null;
        const [sy, sm, sd] = String(detail.startDt).split('-').map(Number);
        const [ey, em, ed] = String(detail.endDt).split('-').map(Number);
        const start = new Date(sy, sm - 1, sd);
        const end   = new Date(ey, em - 1, ed);
        if (end < start) return null;
        let count = 0;
        const cur = new Date(start);
        while (cur <= end) {
            const dow = cur.getDay();
            if (dow !== 0 && dow !== 6) count++;
            cur.setDate(cur.getDate() + 1);
        }
        return count;
    })();

    if (!show) return null;

    const btn = (variant, label, onClick, disabled) => {
        const styles = {
            secondary: { bg: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e' },
            'outline-secondary': { bg: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e' },
            'outline-danger': { bg: 'white', border: '1.5px solid #fca5a5', color: '#dc2626' },
            success: { bg: '#16a34a', border: 'none', color: 'white' },
            danger: { bg: '#dc2626', border: 'none', color: 'white' },
        };
        const s = styles[variant] || styles.secondary;
        return (
            <button onClick={onClick} disabled={disabled} style={{
                height: 34, padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
                background: disabled ? '#f4f3f9' : s.bg, border: s.border,
                color: disabled ? '#aaa9b7' : s.color, opacity: disabled ? 0.7 : 1,
            }}>{label}</button>
        );
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1050,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
        }} onClick={onHide}>
            <div style={{
                background: 'white', borderRadius: 16,
                boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                width: '100%', maxWidth: 680,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: '1px solid #ececf2', flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>
                            {loading ? '불러오는 중...' : (detail?.title || '문서 상세')}
                        </span>
                        {statusCfg && <StatusBadge status={detail.status}/>}
                    </div>
                    <button onClick={onHide} style={{
                        border: 'none', background: 'none', fontSize: 18, cursor: 'pointer',
                        color: '#7a7989', padding: 0, lineHeight: 1,
                    }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                            <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }}/>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : !detail ? (
                        <p style={{ textAlign: 'center', color: '#aaa9b7', padding: '32px 0', margin: 0, fontSize: 13 }}>
                            문서를 불러올 수 없습니다.
                        </p>
                    ) : (
                        <>
                            {/* 결재선 */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#526D82', marginBottom: 6 }}>결재선</div>
                                <div style={{
                                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
                                    padding: '10px 12px', backgroundColor: '#f8f9fa',
                                    borderRadius: 6, border: '1px solid #dee2e6', minHeight: 52,
                                }}>
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        padding: '4px 10px', borderRadius: 4, backgroundColor: '#526D82',
                                        color: '#fff', fontSize: '0.75rem', minWidth: 60, textAlign: 'center',
                                    }}>
                                        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>기안자</span>
                                        <span style={{ fontWeight: 600 }}>{detail.drafterName || '-'}</span>
                                    </div>
                                    {(detail.approvers || []).map((a, i) => {
                                        const st = STATUS_CONFIG[a.status] || STATUS_CONFIG.PENDING;
                                        return (
                                            <React.Fragment key={a.approverEmail}>
                                                <span style={{ color: '#aaa', fontSize: '1rem' }}>›</span>
                                                <div style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                    padding: '4px 8px', borderRadius: 4,
                                                    backgroundColor: '#fff', border: `1px solid ${st.border}`,
                                                    fontSize: '0.75rem', minWidth: 64, textAlign: 'center',
                                                }}>
                                                    <span style={{ fontSize: '0.65rem', color: '#888' }}>결재자 {i + 1}</span>
                                                    <span style={{ fontWeight: 600, color: '#333' }}>{a.approverName || a.approverEmail}</span>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 3,
                                                        fontSize: '0.6rem', fontWeight: 600, color: st.color,
                                                        background: st.bg, padding: '1px 6px', borderRadius: 999, marginTop: 2,
                                                    }}>
                                                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: st.dot, display: 'inline-block' }}/>
                                                        {st.label}
                                                    </span>
                                                    {a.actionAt && (
                                                        <span style={{ fontSize: '0.6rem', color: '#aaa', marginTop: 1 }}>
                                                            {a.actionAt.substring(0, 10)}
                                                        </span>
                                                    )}
                                                    {a.status === 'REJECTED' && a.comment && (
                                                        <span style={{ fontSize: '0.6rem', color: '#dc3545', marginTop: 2, maxWidth: 120, wordBreak: 'break-all', whiteSpace: 'pre-wrap', textAlign: 'center' }}>
                                                            {a.comment}
                                                        </span>
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* 문서 상세 테이블 */}
                            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6', fontSize: '0.875rem' }}>
                                <tbody>
                                    <DocRow>
                                        <Lbl>신청자</Lbl><Val>{detail.drafterName || '-'}</Val>
                                        <Lbl>휴가 구분</Lbl><Val>{LEAVE_TYPE_LABEL[detail.leaveType] || detail.leaveType || '-'}</Val>
                                    </DocRow>
                                    <DocRow>
                                        <Lbl>시작일</Lbl><Val>{detail.startDt || '-'}</Val>
                                        <Lbl>종료일</Lbl><Val>{detail.endDt || '-'}</Val>
                                    </DocRow>
                                    {workingDays !== null && (
                                        <DocRow>
                                            <Lbl>기간</Lbl>
                                            <Val colSpan={3}>
                                                <span style={{ color: '#526D82', fontWeight: 600 }}>총 {workingDays}일</span>
                                                <span style={{ fontSize: '0.75rem', color: '#aaa', marginLeft: 6 }}>(주말 제외)</span>
                                            </Val>
                                        </DocRow>
                                    )}
                                    <DocRow>
                                        <Lbl>사유</Lbl>
                                        <Val colSpan={3} style={{ whiteSpace: 'pre-wrap' }}>{detail.reason || '-'}</Val>
                                    </DocRow>
                                    {(detail.agentName || detail.agentEmail) && (
                                        <DocRow>
                                            <Lbl>업무 대행자</Lbl>
                                            <Val colSpan={3}>{detail.agentName || detail.agentEmail}</Val>
                                        </DocRow>
                                    )}
                                    {detail.emergencyContact && (
                                        <DocRow>
                                            <Lbl>긴급 연락처</Lbl><Val>{detail.emergencyContact}</Val>
                                            <Lbl>관계</Lbl><Val>{detail.emergencyRelation || '-'}</Val>
                                        </DocRow>
                                    )}
                                    {detail.remark && (
                                        <DocRow>
                                            <Lbl>비고</Lbl>
                                            <Val colSpan={3} style={{ whiteSpace: 'pre-wrap' }}>{detail.remark}</Val>
                                        </DocRow>
                                    )}
                                    {detail.submittedAt && (
                                        <DocRow>
                                            <Lbl>제출일시</Lbl>
                                            <Val colSpan={3}>{detail.submittedAt.substring(0, 16).replace('T', ' ')}</Val>
                                        </DocRow>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>

                {/* Reject comment textarea */}
                {mode === 'inbox' && !loading && detail && rejectMode && isMyTurnPending && (
                    <div style={{ padding: '0 20px 12px', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>
                            반려 사유 <span style={{ fontWeight: 400, color: '#888' }}>(선택)</span>
                        </div>
                        <textarea rows={3} placeholder="반려 사유를 입력하세요."
                            value={rejectComment} onChange={e => setRejectComment(e.target.value)}
                            style={{
                                width: '100%', borderRadius: 8, border: '1.5px solid #ececf2',
                                padding: '8px 12px', fontSize: 13, color: '#1f1d2e',
                                outline: 'none', fontFamily: 'inherit', resize: 'vertical',
                                boxSizing: 'border-box', boxShadow: 'none',
                            }}/>
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #ececf2',
                    display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
                }}>
                    {!rejectMode && btn('secondary', '닫기', onHide, false)}
                    {!rejectMode && !loading && detail && btn('outline-secondary', copying ? '복사 중...' : '복사하여 임시 저장', handleCopy, acting || copying)}
                    {mode === 'draft' && !loading && detail?.status === 'DRAFT' && btn('outline-danger', acting ? '삭제 중...' : '삭제', handleDelete, acting)}
                    {mode === 'inbox' && !loading && detail && !rejectMode && isMyTurnPending && (
                        <>
                            {btn('outline-danger', '반려', () => setRejectMode(true), acting)}
                            {btn('success', acting ? '처리 중...' : '승인', handleApprove, acting)}
                        </>
                    )}
                    {mode === 'inbox' && !loading && detail && rejectMode && isMyTurnPending && (
                        <>
                            {btn('secondary', '취소', () => { setRejectMode(false); setRejectComment(''); }, acting)}
                            {btn('danger', acting ? '처리 중...' : '반려 확인', handleRejectConfirm, acting)}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
